<template>
    <div class="max-w-4xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Report Card
                </h1>
                <p
                    class="text-sm text-gray-500 dark:text-gray-400 mt-1"
                    v-if="card"
                >
                    {{ card.student?.user?.name }} · {{ card.class?.name }} ·
                    {{ card.semester?.name }}
                </p>
            </div>
            <button
                @click="goBack"
                class="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
                Back
            </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
            <div
                class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"
            ></div>
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <p class="text-sm text-danger-700 dark:text-danger-300">
                {{ error }}
            </p>
        </div>

        <template v-if="!loading && card">
            <!-- Scores -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                        <p
                            class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                        >
                            Assignment Average
                        </p>
                        <p
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            {{ card.assignment_average ?? "-" }}
                        </p>
                    </div>
                    <div>
                        <p
                            class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                        >
                            Exam Average
                        </p>
                        <p
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            {{ card.exam_average ?? "-" }}
                        </p>
                    </div>
                    <div>
                        <p
                            class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                        >
                            Attendance
                        </p>
                        <p
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            {{
                                card.attendance_percentage !== null &&
                                card.attendance_percentage !== undefined
                                    ? card.attendance_percentage + "%"
                                    : "-"
                            }}
                        </p>
                    </div>
                    <div>
                        <p
                            class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                        >
                            Final Score
                        </p>
                        <p
                            class="text-lg font-bold text-primary-600 dark:text-primary-400"
                        >
                            {{
                                card.final_score !== null &&
                                card.final_score !== undefined
                                    ? `${card.final_score} (${card.grade})`
                                    : "-"
                            }}
                        </p>
                    </div>
                    <div>
                        <p
                            class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                        >
                            Rank
                        </p>
                        <p
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            {{
                                card.rank_in_class
                                    ? `${card.rank_in_class} / ${card.total_students}`
                                    : "-"
                            }}
                        </p>
                    </div>
                    <div>
                        <p
                            class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                        >
                            Status
                        </p>
                        <span
                            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full mt-1"
                            :class="
                                card.is_published
                                    ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                    : 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200'
                            "
                        >
                            {{ card.is_published ? "Published" : "Draft" }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Comments -->
            <div
                class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4"
            >
                <h2 class="text-sm font-semibold text-gray-900 dark:text-white">
                    Comments & Behavior
                </h2>

                <div v-if="canEdit">
                    <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                        Homeroom Teacher Comments
                    </label>
                    <textarea
                        v-model="editForm.teacher_comments"
                        rows="3"
                        class="form-input w-full"
                    ></textarea>
                </div>
                <div v-else>
                    <p
                        class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                    >
                        Homeroom Teacher Comments
                    </p>
                    <p
                        class="text-sm text-gray-900 dark:text-white whitespace-pre-line"
                    >
                        {{ card.teacher_comments || "-" }}
                    </p>
                </div>

                <div v-if="isAdmin">
                    <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                        Principal Comments
                    </label>
                    <textarea
                        v-model="editForm.principal_comments"
                        rows="3"
                        class="form-input w-full"
                    ></textarea>
                </div>
                <div v-else>
                    <p
                        class="text-xs text-gray-500 dark:text-gray-400 uppercase"
                    >
                        Principal Comments
                    </p>
                    <p
                        class="text-sm text-gray-900 dark:text-white whitespace-pre-line"
                    >
                        {{ card.principal_comments || "-" }}
                    </p>
                </div>

                <div v-if="canEdit" class="max-w-xs">
                    <label
                        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                    >
                        Behavior Grade
                    </label>
                    <SearchableSelect
                        v-model="editForm.behavior_grade"
                        :options="behaviorOptions"
                        placeholder="Select Grade"
                        option-label="label"
                        option-value="value"
                    />
                </div>

                <div v-if="canEdit" class="flex justify-end gap-3">
                    <button
                        v-if="canUseAi"
                        @click="generateComments"
                        :disabled="aiGenerating"
                        class="btn border border-primary-600 text-primary-600 hover:bg-primary-50 disabled:opacity-50 dark:border-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20"
                    >
                        {{ aiGenerating ? "Generating..." : "AI Generate" }}
                    </button>
                    <button
                        @click="saveComments"
                        :disabled="saving"
                        class="btn btn-primary disabled:opacity-50"
                    >
                        {{ saving ? "Saving..." : "Save Comments" }}
                    </button>
                </div>
            </div>

            <!-- Actions -->
            <div
                v-if="canPublish"
                class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 flex flex-wrap gap-3"
            >
                <button
                    v-if="!card.is_published"
                    @click="publish"
                    :disabled="actionLoading"
                    class="btn btn-primary disabled:opacity-50"
                >
                    Publish Report Card
                </button>
                <button
                    v-else
                    @click="unpublish"
                    :disabled="actionLoading"
                    class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
                >
                    Unpublish
                </button>
                <button
                    @click="downloadPdf"
                    :disabled="actionLoading"
                    class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50"
                >
                    <Download class="w-4 h-4" />
                    Download PDF
                </button>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Download } from "@lucide/vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import reportCardApi, { ReportCard } from "@/api/reportCard";
import aiService from "@/api/ai";
import { runAiTask } from "@/composables/useAiTask";
import { useAuthStore } from "@/stores/authStore";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const cardId = route.params.id as string;

const card = ref<ReportCard | null>(null);
const loading = ref(false);
const saving = ref(false);
const actionLoading = ref(false);
const error = ref<string | null>(null);

const editForm = ref({
    teacher_comments: "",
    principal_comments: "",
    behavior_grade: null as string | null,
});

const behaviorOptions = [
    { label: "A (Excellent)", value: "A" },
    { label: "B (Good)", value: "B" },
    { label: "C (Sufficient)", value: "C" },
    { label: "D (Needs Improvement)", value: "D" },
];

const isAdmin = computed(() =>
    authStore.hasAnyRole(["administrator", "super-administrator"]),
);

const isStudent = computed(() => authStore.hasRole("student"));

const canEdit = computed(() => !isStudent.value);

const canPublish = computed(() => !isStudent.value);

const canUseAi = computed(() => authStore.hasPermission("ai-assistant.use"));

// AI comment drafts fill the textareas; nothing is saved until the
// homeroom teacher clicks Save Comments.
const aiGenerating = ref(false);

async function generateComments() {
    if (aiGenerating.value) return;

    aiGenerating.value = true;

    try {
        const comments = await runAiTask<{
            teacher_comment: string;
            principal_comment: string;
        }>(() => aiService.suggestReportComments(cardId));
        editForm.value.teacher_comments = comments.teacher_comment;
        editForm.value.principal_comments = comments.principal_comment;
    } catch (err: any) {
        error.value =
            err.response?.data?.message ||
            err.message ||
            "Failed to generate AI comment drafts";
    } finally {
        aiGenerating.value = false;
    }
}

async function fetchCard() {
    loading.value = true;
    error.value = null;

    try {
        card.value = await reportCardApi.getById(cardId);
        editForm.value = {
            teacher_comments: card.value.teacher_comments || "",
            principal_comments: card.value.principal_comments || "",
            behavior_grade: card.value.behavior_grade || null,
        };
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch report card";
    } finally {
        loading.value = false;
    }
}

async function saveComments() {
    saving.value = true;
    error.value = null;

    try {
        card.value = await reportCardApi.update(cardId, {
            teacher_comments: editForm.value.teacher_comments,
            principal_comments: editForm.value.principal_comments,
            behavior_grade: editForm.value.behavior_grade || undefined,
        });
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to save comments";
    } finally {
        saving.value = false;
    }
}

async function publish() {
    actionLoading.value = true;

    try {
        card.value = await reportCardApi.publish(cardId);
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to publish report card";
    } finally {
        actionLoading.value = false;
    }
}

async function unpublish() {
    actionLoading.value = true;

    try {
        card.value = await reportCardApi.unpublish(cardId);
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to unpublish report card";
    } finally {
        actionLoading.value = false;
    }
}

function downloadPdf() {
    window.open(reportCardApi.getPdfStreamUrl(cardId), "_blank");
}

function goBack() {
    router.push("/reports");
}

onMounted(() => {
    fetchCard();
});
</script>
